import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import StatTile from '../shared/StatTile';
import DataTable from '../shared/DataTable';
import Pagination, { normalizePagination } from '../shared/Pagination';

// Same-origin Vercel serverless functions (api/kobo/*.js) — not the Express
// backend. They hold the Kobo token server-side and proxy kf.kobotoolbox.org,
// which has no CORS headers and can't be called straight from the browser.
const KOBO_API_BASE = '/api/kobo';

const EMPTY_FILTERS = { startDate: '', endDate: '', enumerator: '' };

// Fields kept out of the auto-generated columns: attachments/geolocation are
// bulky nested blobs that make the table unreadable; the rest are shown.
const HIDDEN_KEYS = new Set(['_attachments', '_geolocation', '_notes', '_xform_id_string']);
const PRIORITY_KEYS = ['_id', '_submission_time', '_submitted_by', '_status'];

const formatValue = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const buildColumns = (rows) => {
  const keySet = new Set();
  rows.forEach((r) => Object.keys(r).forEach((k) => { if (!HIDDEN_KEYS.has(k)) keySet.add(k); }));
  const keys = Array.from(keySet).sort((a, b) => {
    const ai = PRIORITY_KEYS.indexOf(a);
    const bi = PRIORITY_KEYS.indexOf(b);
    if (ai !== -1 || bi !== -1) return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    return a.localeCompare(b);
  });
  return keys.map((k) => ({ key: k, label: k, render: (row) => formatValue(row[k]) }));
};

export default function LiveApiData() {
  const [temp, setTemp] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [enumerators, setEnumerators] = useState([]);

  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(normalizePagination({}));

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${KOBO_API_BASE}/enumerators`);
        if (active && res.data?.success) setEnumerators(res.data.enumerators || []);
      } catch (err) {
        console.error('Error fetching enumerators:', err);
      }
    })();
    return () => { active = false; };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: pageSize };
      if (applied.startDate) params.startDate = applied.startDate;
      if (applied.endDate) params.endDate = applied.endDate;
      if (applied.enumerator) params.enumerator = applied.enumerator;
      const res = await axios.get(`${KOBO_API_BASE}/live-data`, { params });
      if (res.data?.success) {
        const results = res.data.data || [];
        setRows(results);
        setColumns(buildColumns(results));
        setPagination(normalizePagination(res.data.pagination || {}));
      }
    } catch (err) {
      console.error('Error fetching live API data:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to fetch live data.');
      setRows([]);
      setColumns([]);
      setPagination(normalizePagination({}));
    } finally {
      setLoading(false);
    }
  }, [applied, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleField = (e) => setTemp((p) => ({ ...p, [e.target.name]: e.target.value }));
  const handleApply = () => { setApplied(temp); setPage(1); };
  const handleClear = () => { setTemp(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); setPage(1); };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">
          <CloudSyncIcon /> Live API Data
        </h1>
        <p className="page-subtitle">
          Live submissions pulled directly from the KoboToolbox form, filterable by submission date and enumerator.
        </p>
      </div>

      <div className="filterbar">
        <div className="filterbar-head">
          <CloudSyncIcon fontSize="small" /> Filter by Submission Date &amp; Enumerator
        </div>
        <div className="filter-grid">
          <div className="filter-group">
            <label>Start Date</label>
            <input type="date" name="startDate" value={temp.startDate} onChange={handleField} />
          </div>
          <div className="filter-group">
            <label>End Date</label>
            <input type="date" name="endDate" value={temp.endDate} onChange={handleField} />
          </div>
          <div className="filter-group">
            <label>Enumerator</label>
            <select name="enumerator" value={temp.enumerator} onChange={handleField}>
              <option value="">All enumerators</option>
              {enumerators.map((e) => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="filter-actions">
          <button className="btn btn-primary" onClick={handleApply} disabled={loading}>
            <SearchIcon fontSize="small" /> {loading ? 'Applying…' : 'Apply Filter'}
          </button>
          <button className="btn btn-secondary" onClick={handleClear} disabled={loading}>
            <ClearIcon fontSize="small" /> Clear
          </button>
        </div>
      </div>

      <div className="stat-tile-row">
        <StatTile label="Total Submissions" value={pagination.total} icon={CloudSyncIcon} accent="var(--primary)" />
        <StatTile label="Showing" value={rows.length} icon={CloudSyncIcon} accent="var(--info)" sub={`page ${pagination.page} of ${pagination.totalPages}`} />
      </div>

      {error ? <div className="empty-state">{error}</div> : null}

      <div className="card">
        <h3 className="card-title">Submissions</h3>
        <Pagination
          pagination={pagination}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          unitLabel="submissions"
          shown={rows.length}
        />
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          emptyMessage='No submissions found. Adjust the filters and click "Apply Filter".'
          getRowKey={(r, i) => r._id ?? r._uuid ?? i}
        />
      </div>
    </div>
  );
}
